/**
 * 监控看板数据查询
 *
 * 数据来自 api-server GraphQL，后端经 Dubbo 转发至 swiftx-quality-service，
 * 最终读 pod_exempt_metric_daily 的窗口聚合。
 *
 * 列表与统计卡并发发起并共用同一份筛选条件：口径若各写一套，
 * 会出现「卡片说 3 个异常、列表筛出 5 行」的对不上。
 *
 * 地址、邮编、仓、DSP 等展示字段来自主 R 的 I1 生效清单，由服务端补齐后下发。
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import {useApolloClient} from '@apollo/client';
import {message} from 'antd';
import {translateGraphQLError} from 'utils/graphqlUtils';
import {MonitorFilters, MonitorRow, MonitorStats} from '../types/ExemptSpotCheckType';
import {
    GET_MONITOR_ROWS,
    GET_MONITOR_STATS,
    GetMonitorRowsResponse,
    GetMonitorStatsResponse,
} from '../graphql/queries/monitorQueries';
import {toMonitorRows} from '../graphql/monitorConverter';

interface UseExemptMonitorQueryResult {
    rows: MonitorRow[];
    stats?: MonitorStats;
    total: number;
    loading: boolean;
    executeQuery: (filters: MonitorFilters, page: number, pageSize: number) => void;
}

interface MonitorFiltersInput {
    targetType?: string;
    monitorLevel?: string;
}

/**
 * 前端筛选条件 → 后端入参。
 *
 * 不传日期：服务端按「截至昨日的近 7 日」兜底。当日指标由日任务次日凌晨才落全，
 * 前端主动传含当日的窗口会让看板长期显示一个不完整的今天。
 */
const buildFiltersInput = (filters: MonitorFilters): MonitorFiltersInput => ({
    targetType: filters.targetType,
    monitorLevel: filters.monitorLevel,
});

export const useExemptMonitorQuery = (): UseExemptMonitorQueryResult => {
    const client = useApolloClient();
    const [rows, setRows] = useState<MonitorRow[]>([]);
    const [stats, setStats] = useState<MonitorStats | undefined>(undefined);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    /** 请求序号：翻页时前一次请求可能仍在途，后到的旧响应会覆盖新结果。只认最后一次发起的 */
    const requestSeq = useRef(0);

    const executeQuery = useCallback(
        (filters: MonitorFilters, page: number, pageSize: number) => {
            const seq = ++requestSeq.current;
            setLoading(true);
            const input = buildFiltersInput(filters);

            const listPromise = client.query<GetMonitorRowsResponse>({
                query: GET_MONITOR_ROWS,
                variables: {filters: input, pageNum: page, pageSize},
                fetchPolicy: 'network-only',
            });
            const statsPromise = client.query<GetMonitorStatsResponse>({
                query: GET_MONITOR_STATS,
                variables: {filters: input},
                fetchPolicy: 'network-only',
            });

            Promise.all([listPromise, statsPromise])
                .then(([listRes, statsRes]) => {
                    if (seq !== requestSeq.current) {
                        return;
                    }
                    const pageData = listRes.data?.podExemptMonitorRows;
                    setRows(toMonitorRows(pageData?.content ?? []));
                    setTotal(pageData?.total ?? 0);

                    const statsData = statsRes.data?.podExemptMonitorStats;
                    setStats(
                        statsData && {
                            monitoringCount: statsData.monitoringCount,
                            anomalyCount: statsData.anomalyCount,
                            orderCount7d: statsData.orderCount ?? undefined,
                            complaintCount7d: statsData.complaintCount ?? undefined,
                        },
                    );
                })
                .catch((e) => {
                    if (seq !== requestSeq.current) {
                        return;
                    }
                    // 不静默：查询失败时清空并提示，否则页面与「暂无数据」无从区分
                    setRows([]);
                    setTotal(0);
                    setStats(undefined);
                    message.error(translateGraphQLError(e, 'exemptSpotCheck.messages.loadMonitorFailed'));
                })
                .finally(() => {
                    if (seq === requestSeq.current) {
                        setLoading(false);
                    }
                });
        },
        [client],
    );

    useEffect(() => {
        executeQuery({}, 1, 20);
    }, [executeQuery]);

    return {rows, stats, total, loading, executeQuery};
};
